import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { brand } from '../brand.js';
import { MOTION, ensureMotionStyles, useEntrance, usePrefersReducedMotion } from './motion.jsx';

export const PALETTE = brand.palette;
export const NEUTRAL = brand.neutral;
export const SEMANTIC = brand.semantic;
// `_1d` — one decimal, but drop a trailing ".0" so round axis ticks
// read clean ("2K", not "2.0K"; "58.7K" keeps its decimal). Axis
// polish 2026-07 — pure formatting, values untouched.
const _1d = n => n.toFixed(1).replace(/\.0$/, '').replace(/^-0$/, '0');
export const fmt  = n => n >= 1e6 ? `$${_1d(n/1e6)}M` : n >= 1e3 ? `$${_1d(n/1e3)}K` : `$${n}`;
// Magnitude thresholds on |n| so NEGATIVE round ticks (diverging /
// waterfall axes) abbreviate like their positive mirrors ("-1.5K",
// not "-1500").
export const fmtN = n => Math.abs(n) >= 1e6 ? `${_1d(n/1e6)}M` : Math.abs(n) >= 1e3 ? `${_1d(n/1e3)}K` : String(Math.round(n));
export const pct  = n => `${(n*100).toFixed(1)}%`;

// ── Configurable value formatter (data labels + axes) ────────
//
// `formatValue(value, fmt, decimals)` — single-call formatter shared
// by every chart's data labels and axes. Mirrors the semantics of
// the editor's "Etiquetas y formato" sub-form:
//
//   fmt:
//     'raw'      → 12345.6  (just the number)
//     'compact'  → 12.3K | 1.2M | 1.2B (thousands suffix)
//     'currency' → $12.3K | $1.2M (compact $ prefix)
//     'percent'  → 12.3%   (input expected in 0..1)
//
//   decimals: 0..2 — clamps `.toFixed(n)` for compact / currency /
//   percent. Integers in 'compact' / 'currency' below 1k drop
//   decimals automatically.
//
// Defensive: returns '—' for null/undefined, '0' for non-finite.
export function formatValue(value, fmt = 'raw', decimals = 1) {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  const d = Math.max(0, Math.min(2, Number.isFinite(+decimals) ? +decimals : 1));
  switch (fmt) {
    case 'compact': {
      const a = Math.abs(n);
      if (a >= 1e9) return `${(n / 1e9).toFixed(d)}B`;
      if (a >= 1e6) return `${(n / 1e6).toFixed(d)}M`;
      if (a >= 1e3) return `${(n / 1e3).toFixed(d)}K`;
      return d > 0 ? n.toFixed(d) : String(Math.round(n));
    }
    case 'currency': {
      const a = Math.abs(n);
      if (a >= 1e9) return `$${(n / 1e9).toFixed(d)}B`;
      if (a >= 1e6) return `$${(n / 1e6).toFixed(d)}M`;
      if (a >= 1e3) return `$${(n / 1e3).toFixed(d)}K`;
      return `$${d > 0 ? n.toFixed(d) : Math.round(n)}`;
    }
    case 'percent':
      return `${(n * 100).toFixed(d)}%`;
    case 'raw':
    default:
      return d > 0 ? n.toFixed(d) : String(Math.round(n));
  }
}

// Build a (value) => string formatter from an editor spec.
// `spec` is { format, decimals } | null. Falls back to `fallbackFn`
// when spec is missing/empty so the chart's existing label styling
// stays intact for unconfigured charts.
export function resolveFormatter(spec, fallbackFn = fmtN) {
  if (!spec || typeof spec !== 'object') return fallbackFn;
  const f = spec.format;
  if (!f || f === 'auto') return fallbackFn;
  const d = spec.decimals;
  return (v) => formatValue(v, f, d);
}

// ── Label resolution (redesign rule 1) ───────────────────────
//
// Build the (value)=>string formatter for data labels from an editor
// spec. Returns `null` when the spec disables labels — callers can
// use the truthiness as the "render label?" gate.
function buildLabelFmt(spec) {
  if (!spec || typeof spec !== 'object' || spec.show !== true) return null;
  const f = spec.format ?? 'compact';
  const d = spec.decimals;
  return (v) => formatValue(v, f, d);
}

// Data labels ON by default ("somos BI"). Precedence: the editor's
// `dataLabels` spec always wins when present (`show: true` → its
// formatter, `show: false` → off). Otherwise the `showValues` prop
// (default true) drives visibility with `fallbackFmt` as the
// formatter. Returns the formatter or null. Single implementation
// shared by the interactive AND static chart catalogs.
export function resolveLabels(dataLabels, showValues, fallbackFmt = fmtN) {
  const explicit = buildLabelFmt(dataLabels);
  if (explicit) return explicit;
  if (dataLabels && typeof dataLabels === 'object' && dataLabels.show === false) return null;
  return showValues === false ? null : fallbackFmt;
}

// Last value + fractional delta vs the previous point of a series —
// feeds the legend-table (redesign rule 3). Delta is null whenever it
// can't be computed honestly (fewer than 2 points, non-finite values,
// previous === 0) — the chip is simply omitted, never invented.
export function lastDelta(arr) {
  const n = Array.isArray(arr) ? arr.length : 0;
  const last = n > 0 ? arr[n - 1] : null;
  const prev = n > 1 ? arr[n - 2] : null;
  const lastOk = typeof last === 'number' && Number.isFinite(last);
  const prevOk = typeof prev === 'number' && Number.isFinite(prev) && prev !== 0;
  return {
    value: lastOk ? last : null,
    delta: lastOk && prevOk ? (last - prev) / Math.abs(prev) : null,
  };
}

// ── Trend colors (consistent across KPIs / tables / charts) ───
// Returns tailwind classes for positive / negative / neutral deltas.
// Use `subtle` for pill backgrounds, `bold` for solid text.
export function trendClasses(delta, variant = 'pill') {
  const up = delta > 0, down = delta < 0;
  if (variant === 'text') {
    return up ? 'text-positive' : down ? 'text-negative' : 'text-neutral-500';
  }
  return up
    ? 'bg-emerald-50 text-positive'
    : down
      ? 'bg-red-50 text-negative'
      : 'bg-neutral-100 text-neutral-500';
}

// ── Chart Legend (reusable) ──────────────────────────────────
// items: [{ label, color, shape? = 'square' | 'line' | 'dot' }]
export function ChartLegend({ items = [], className = '' }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 ${className}`}>
      {items.map((it, i) => (
        <span key={i} className="inline-flex items-center gap-1.5 text-[11px] text-neutral-500">
          {it.shape === 'line' ? (
            <svg width="14" height="4" aria-hidden="true"><line x1="0" y1="2" x2="14" y2="2" stroke={it.color} strokeWidth="2.5" strokeLinecap="round"/></svg>
          ) : it.shape === 'dot' ? (
            <span className="h-2 w-2 rounded-full inline-block" style={{ background: it.color }} aria-hidden="true" />
          ) : (
            <span className="h-2.5 w-2.5 rounded-sm inline-block" style={{ background: it.color }} aria-hidden="true" />
          )}
          <span className="font-medium">{it.label}</span>
        </span>
      ))}
    </div>
  );
}

// ── Chart Empty State ────────────────────────────────────────
// Drop-in replacement when a chart receives no data.
export function ChartEmpty({ height = 200, message = 'Sin datos disponibles' }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center justify-center gap-2 w-full rounded-lg border border-dashed border-neutral-200 bg-neutral-50/50 text-neutral-500"
      style={{ height }}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
        <path d="M3 3v18h18"/><path d="M7 14l3-3 4 4 5-5"/>
      </svg>
      <span className="text-xs font-medium">{message}</span>
    </div>
  );
}

export const hasData = (d) => Array.isArray(d) && d.length > 0;

// ── Responsive container width hook ──────────────────────────
// Attaches a ResizeObserver to a wrapper div and returns its CSS pixel width.
// useLayoutEffect fires synchronously after DOM insertion, before paint, so
// the first paint already uses the measured width — no flash at wrong size.
// `fallback` is used on SSR / before first measurement.
export function useContainerWidth(fallback = 400) {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const measure = () => {
      const w = Math.round(ref.current.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    measure(); // immediate measurement on mount
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// ── monotonePath — smooth line interpolation without overshoot ──
//
// Builds an SVG path string through `points` ([{x, y}]) using
// monotone-cubic (Fritsch–Carlson) interpolation. Unlike Catmull-Rom
// or plain cubic smoothing, the curve NEVER overshoots the data — it
// won't draw values that don't exist between two points. Dots placed
// at the raw points mark the real data (redesign rule 6).
//
// Single implementation for the whole catalog: LineChart(T),
// MultiLine(T), ComboChart(T), StackedArea, Sparkline, LineTrend,
// KPISparkline, TimelineChart and the DivergingStackedBar net line.
//
// Defensive: empty → '', 1 point → move-to, 2 points → straight line.
// Duplicate consecutive x values get an epsilon so slopes stay finite.
export function monotonePath(points) {
  const pts = (points || []).filter(
    p => p && Number.isFinite(p.x) && Number.isFinite(p.y),
  );
  const n = pts.length;
  if (n === 0) return '';
  const r = v => +v.toFixed(2);
  if (n === 1) return `M${r(pts[0].x)},${r(pts[0].y)}`;
  if (n === 2) return `M${r(pts[0].x)},${r(pts[0].y)}L${r(pts[1].x)},${r(pts[1].y)}`;

  // Secant slopes between consecutive points.
  const dx = [], m = [];
  for (let i = 0; i < n - 1; i++) {
    const d = pts[i + 1].x - pts[i].x;
    dx[i] = d === 0 ? 1e-6 : d;
    m[i] = (pts[i + 1].y - pts[i].y) / dx[i];
  }
  // Tangents (Fritsch–Carlson): zero at sign changes, clamped so the
  // interpolant stays monotone on each interval (no overshoot).
  const t = [m[0]];
  for (let i = 1; i < n - 1; i++) {
    t[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2;
  }
  t[n - 1] = m[n - 2];
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; continue; }
    const a = t[i] / m[i], b = t[i + 1] / m[i];
    const s = a * a + b * b;
    if (s > 9) {
      const tau = 3 / Math.sqrt(s);
      t[i] = tau * a * m[i];
      t[i + 1] = tau * b * m[i];
    }
  }
  // Hermite → cubic Bézier segments.
  let d = `M${r(pts[0].x)},${r(pts[0].y)}`;
  for (let i = 0; i < n - 1; i++) {
    const c1x = pts[i].x + dx[i] / 3, c1y = pts[i].y + (t[i] * dx[i]) / 3;
    const c2x = pts[i + 1].x - dx[i] / 3, c2y = pts[i + 1].y - (t[i + 1] * dx[i]) / 3;
    d += `C${r(c1x)},${r(c1y)} ${r(c2x)},${r(c2y)} ${r(pts[i + 1].x)},${r(pts[i + 1].y)}`;
  }
  return d;
}

// ── niceTicks — round axis tick values (2026-07 axis polish) ──
//
// Classic "nice numbers" axis algorithm. Instead of labelling the Y
// axis at fractions of the data max ([0, .25, .5, .75, 1] × yMax →
// ugly values like 604 / 9.1K / 27.2K), pick a step from
// {1, 2, 2.5, 5} × 10^n sized to cover the range in ~`targetCount`
// intervals and emit ticks on multiples of that step (10/20/30,
// 2/4/6, 1K/2K/3K…).
//
// The axis DOMAIN extends to the ticks: `max` is the first tick ≥
// dataMax and `min` the last tick ≤ dataMin — callers must scale
// their geometry against the returned `min`/`max`, not the raw data
// extremes (the old ×1.1 / ×1.15 headroom factors are superseded by
// the round-up to the next tick).
//
//   niceTicks(dataMax, targetCount = 5, dataMin = 0)
//     → { ticks: number[], min, max, step }
//
// `dataMin` defaults to 0 (bar-family charts always anchor at zero).
// Line-family charts pass their real data min so the scale stays
// tight (min becomes the nice FLOOR ≤ dataMin — possibly negative,
// e.g. waterfall). Defensive: non-finite / degenerate input yields
// the [0, 1] axis rather than NaN geometry.
export function niceStep(rough) {
  if (!Number.isFinite(rough) || rough <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const frac = rough / pow;
  const mult = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10;
  return mult * pow;
}

export function niceTicks(dataMax, targetCount = 5, dataMin = 0) {
  let hi = Number.isFinite(dataMax) ? dataMax : 0;
  let lo = Number.isFinite(dataMin) ? dataMin : 0;
  if (hi < lo) { const t = hi; hi = lo; lo = t; }
  if (hi === lo) { hi = lo + (lo === 0 ? 1 : Math.abs(lo) * 0.1); }
  const count = Math.max(1, Number.isFinite(+targetCount) ? +targetCount : 4);
  const step = niceStep((hi - lo) / count);
  const start = Math.floor(lo / step) * step;
  const end = Math.ceil(hi / step) * step;
  const ticks = [];
  // Float-safe loop: iterate on integer multiples of the step.
  const i0 = Math.round(start / step);
  const i1 = Math.round(end / step);
  for (let i = i0; i <= i1; i++) ticks.push(+((i * step).toPrecision(12)));
  return { ticks, min: ticks[0], max: ticks[ticks.length - 1], step };
}

// Symmetric variant for diverging charts (inflow above zero, outflow
// below): nice ticks on [0..maxAbs] mirrored across zero, so both
// half-planes share identical round gridlines (…, −1K, 0, 1K, …).
// `targetCount` counts intervals PER SIDE.
export function niceSymmetricTicks(maxAbs, targetCount = 2) {
  const { ticks: pos, step } = niceTicks(Math.abs(maxAbs) || 1, targetCount, 0);
  const upper = pos.slice(1); // drop the shared 0
  const ticks = [...upper.map(v => -v).reverse(), 0, ...upper];
  return { ticks, max: upper[upper.length - 1] ?? 1, step };
}

// ── lineDotRadius — density-adaptive line markers (style "C") ──
//
// 2026-07 user decision: line-family point markers are SOLID dots in
// the series color with NO white halo — the halo "perforated" the
// line on dense series. Radius adapts to density: dense series
// (> 40 points) get micro-dots (1.2px), short series keep readable
// dots (2.4px). Endpoints (first/last, where the value labels live)
// carry a slightly larger dot so the series end keeps its emphasis.
// HOVER markers are a separate affordance and keep their big
// white-filled ring. Scatter/bubble are NOT line markers (the point
// IS the datum) and don't use this.
export function lineDotRadius(n, isEndpoint = false) {
  const base = n > 40 ? 1.2 : 2.4;
  return isEndpoint ? Math.max(3, base + 1.4) : base;
}

// ── separateLabelYs — vertical anti-collision for data labels ──
//
// When two series' labels land on the same x position (typically the
// LAST point of close series: "20.9K" over "17.3K"), their y
// positions can overlap. Given the desired y positions of a group of
// labels sharing an x slot, returns adjusted ys at least `minGap`
// apart, clamped to [lo, hi]. The topmost label (largest value) keeps
// its position and lower ones are pushed DOWN (then everything is
// pulled back up if the bottom clamp bites) — deterministic, no
// crossings, order preserved.
export function separateLabelYs(ys, minGap = 12, lo = -Infinity, hi = Infinity) {
  const order = ys
    .map((y, i) => ({ y: Number.isFinite(y) ? y : 0, i }))
    .sort((a, b) => a.y - b.y);
  const placed = new Array(order.length);
  for (let k = 0; k < order.length; k++) {
    const floor = k === 0 ? lo : placed[k - 1] + minGap;
    placed[k] = Math.max(order[k].y, floor, lo);
  }
  // Bottom clamp: walk back up preserving the minimum gap.
  for (let k = order.length - 1; k >= 0; k--) {
    const ceil = k === order.length - 1 ? hi : placed[k + 1] - minGap;
    if (placed[k] > ceil) placed[k] = ceil;
  }
  const out = new Array(ys.length);
  order.forEach((o, k) => { out[o.i] = placed[k]; });
  return out;
}

// ── tintColor — mix a hex color toward white ─────────────────
// `t` in [0..1]: 0 → original color, 1 → white. Used by the ranked
// horizontal-bar style to build the per-rank degradé from a single
// base palette color (redesign rule 5) without hardcoding hex ramps.
export function tintColor(hex, t) {
  const h = String(hex).replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return hex;
  const f = Math.max(0, Math.min(1, t));
  const mix = (i) => {
    const c = parseInt(h.slice(i, i + 2), 16);
    return Math.round(c + (255 - c) * f).toString(16).padStart(2, '0');
  };
  return `#${mix(0)}${mix(2)}${mix(4)}`;
}

// ── Horizontal-bar label channel (feedback 2026-07-14) ───────
// Long category labels (SKU concatenations like "M0H56AL + M0H54AL +
// M0H55AL + 1VV22AL X2") used to clip at the fixed 220px gutter of the
// ranked horizontal bars. The channel is now ADAPTIVE — the balance
// the feedback asked for ("achicar las barras hasta un punto, después
// salto de línea"):
//   1. The label column grows with the longest label up to `maxPx`
//      (callers pass ~38% of the chart width). Bars shrink accordingly
//      but the datum stays readable: the bold value sits at the right
//      edge, outside the track.
//   2. A label that still doesn't fit wraps to TWO lines (<tspan>),
//      broken at the whitespace nearest the middle (handles the
//      " + "-joined SKU lists); unbroken tokens hard-split.
//   3. If even two lines overflow, line 2 is ellipsized and callers
//      put the FULL label in a native <title> tooltip (`truncated`
//      flags it) — the data is never silently unreachable.
// Widths are estimated chars × avg glyph px (same heuristic as the
// existing pad.l auto-grow rules): 6.6px at fontSize 11, single line or
// wrapped (wrapped rows used to render at 10px; the 11 px floor of QA
// look v3 F16 put them at 11 too; two lines still fit the 28px row of
// the `comfortable` density; la densidad `compact` pasa allowWrap=false
// porque su fila de 22px no da para dos lineas).
// Returns { rows, textW }: rows[i] = { lines, full, wrapped, truncated };
// textW = px the label column actually needs POST-wrap (≤ maxPx), so
// bars only shrink as much as the wrapped labels really require.
export const BAR_LABEL_CHAR_PX = 6.6;
export const BAR_LABEL_WRAP_CHAR_PX = 6.6;

// ── Densidad de las barras rankeadas ─────────────────────────
//
// `HorizontalBar` y `HorizontalBarT` son la MISMA geometría escrita dos
// veces (una estática, una con tooltip). Las constantes vivían
// duplicadas en las dos, así que cualquier ajuste de densidad hecho en
// una sola las hacía divergir en silencio. Acá van una vez.
//
// `compact` existe porque siete filas a 28px empujan la tarjeta a 212px
// de alto natural, y en una fila de tres tarjetas eso deja a las otras
// dos con cien y pico de píxeles de blanco. A 22px (19 hasta el piso de
// 11 px del texto, QA F16) las mismas siete filas entran en 170.
//
// Lo que se va en compacto NO es información: es el chip de rank
// numerado. El orden de las filas YA es el ranking, así que el chip lo
// repetía — y sus 28px de ancho (chip + gap) se los devuelve al canal
// de labels, que era el otro apretado ("HISENSE · FOCO" entraba
// cortado). El valor, el share % y el label siguen todos ahí.
export const BAR_DENSITY = {
  comfortable: {
    rowH: 28, chipW: 20, chipGap: 8, chipFs: 11,
    barMax: 16, labelFs: 11, wrapFs: 11, wrapDy: 11.5,
    valueFs: 12, shareFs: 11, valueCharPx: 7.2, shareCharPx: 6.5,
  },
  compact: {
    // Sin chip: chipW 0 y chipGap 0 sacan la columna entera, no la
    // dejan vacía ocupando lugar.
    rowH: 22, chipW: 0, chipGap: 0, chipFs: 0,
    barMax: 10, labelFs: 11, wrapFs: 11, wrapDy: 11,
    valueFs: 11, shareFs: 11, valueCharPx: 6.6, shareCharPx: 6.5,
  },
};

/** Resuelve el nombre de densidad a su métrica.
 *
 *  Un nombre desconocido cae en `comfortable` A PROPÓSITO, y es la ÚNICA
 *  capa donde eso es correcto: igual que `resolveTone`, el render no
 *  tiene ErrorBoundary encima, así que tirar acá sería pantalla en
 *  blanco. El nombre malo se caza en el parse (`discovery`), que es
 *  donde este repo valida los enumerados del config y levanta. */
export function barDensity(name) {
  return BAR_DENSITY[name] || BAR_DENSITY.comfortable;
}

// `pieCenterX` — where PieChart / PieChartT put the pie's centre.
//
// The pie used to sit at a fixed 27.8% of the width with the legend to
// its right. That is right while the radius is width-bound (narrow
// cards), but in a wide card the radius is HEIGHT-bound, so the pie and
// its legend packed into the left half and left a third of the card
// blank on the right (Pablo, 2026-10-19). Now the pie + 22px gap +
// legend are centred as ONE block. The legend width is an ESTIMATE
// (characters × 0.6 × font size, the same glyph ratio as the ranked-bar
// labels) — an SVG cannot measure text before it renders. The old 27.8%
// is the floor: the block never moves left of where it used to sit, so
// a narrow card renders exactly as before and a block that does not fit
// keeps the old layout instead of pushing the legend off the right edge.
export function pieCenterX(w, r, fs, data, total) {
  const legacy = w * 0.278;
  const legendW = 16 + Math.max(0, ...data.map((d) => {
    const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : '0.0';
    const line1 = String(d.label ?? '').length * fs * 0.6;
    const line2 = `${fmtN(d.value)} · ${pct}%`.length * (fs - 1) * 0.6;
    return Math.max(line1, line2);
  }));
  const block = 2 * r + 22 + legendW;
  return Math.max(legacy, (w - block) / 2 + r);
}

export function barLabelLayout(labels, maxPx, minPx = 62, allowWrap = true) {
  const budget = Math.max(minPx, maxPx);
  const wrapChars = Math.max(4, Math.floor(budget / BAR_LABEL_WRAP_CHAR_PX));
  const rows = labels.map((raw) => {
    const full = String(raw ?? '');
    if (full.length * BAR_LABEL_CHAR_PX <= budget) {
      return { lines: [full], full, wrapped: false, truncated: false };
    }
    // Densidad compacta: la fila mide 22px y dos lineas de texto miden
    // ~24px, asi que envolver seria pisar la fila de abajo. Se trunca a
    // una linea y el label completo vive en el <title> — la unica razon
    // por la que truncar es aceptable acá.
    if (!allowWrap) {
      const chars = Math.max(4, Math.floor(budget / BAR_LABEL_CHAR_PX));
      const cut = `${full.slice(0, Math.max(1, chars - 1)).trimEnd()}…`;
      return { lines: [cut], full, wrapped: false, truncated: true };
    }
    // Two-line wrap: among whitespace breaks that keep line 1 within
    // budget, pick the one nearest the middle; prefer breaking right
    // after a "+" joiner (the continuation cue stays on line 1) and
    // breaks where line 2 also fits.
    let cut = -1;
    let best = Infinity;
    for (let i = 1; i < full.length - 1; i++) {
      if (full[i] !== ' ') continue;
      if (i > wrapChars) break; // line 1 would overflow; later breaks only worse
      const line2Fits = full.length - i - 1 <= wrapChars;
      const afterJoiner = full[i - 1] === '+';
      const score = Math.abs(i - full.length / 2) + (line2Fits ? 0 : 1000) - (afterJoiner ? 0.5 : 0);
      if (score < best) { best = score; cut = i; }
    }
    const rawLines = cut >= 0
      ? [full.slice(0, cut), full.slice(cut + 1)]
      : [full.slice(0, wrapChars), full.slice(wrapChars)]; // no usable break — hard split
    const lines = rawLines.map((ln, i) =>
      i === 1 && ln.length > wrapChars
        ? `${ln.slice(0, Math.max(1, wrapChars - 1)).trimEnd()}…`
        : ln,
    );
    return { lines, full, wrapped: true, truncated: lines[1] !== rawLines[1] };
  });
  const widest = rows.reduce(
    (m, r) => Math.max(m, ...r.lines.map((ln) => ln.length * (r.wrapped ? BAR_LABEL_WRAP_CHAR_PX : BAR_LABEL_CHAR_PX))),
    0,
  );
  return { rows, textW: Math.ceil(Math.min(budget, Math.max(minPx, widest))) };
}

// ── HEAT_RAMP — shared heat scale (redesign F3b) ──────────────
// Light→dark magenta ramp (`brand.heatRamp`, the old accent 50 → 900
// ladder kept as a data ramp). Single source for every heat-colored surface:
// maps (SouthAmericaMap / ChileMap), CalendarHeatmap, HeatmapChart
// and HeatmapTable. Replaces the inline morado ramps that used to
// live copy-pasted in maps.jsx / analytics.jsx.
export const HEAT_RAMP = [...brand.heatRamp];

// ── Sentiment + DeltaChip (redesign rule 4) ──────────────────
//
// `resolveSentiment(sentiment, delta)` → 'good' | 'bad' | 'neutral'.
// An explicit `sentiment` prop always wins (metrics where "up is
// bad" — unauthorised sellers, expulsion time — pass it); otherwise
// the SIGN of `delta` decides (up → good, down → bad). Neutral when
// neither exists — never invented.
export function resolveSentiment(sentiment, delta) {
  if (sentiment === 'good' || sentiment === 'bad' || sentiment === 'neutral') return sentiment;
  if (typeof delta === 'number' && Number.isFinite(delta) && delta !== 0) {
    return delta > 0 ? 'good' : 'bad';
  }
  return 'neutral';
}

// `DeltaChip` — the semantic ▲/▼ pastel pill (HTML flavor; SVG
// legends draw their own inside `SVGLegendRows`). The arrow follows
// the SIGN of `delta`; the color follows the resolved sentiment, so
// an inverted metric renders a red ▲ (up is bad). `label` is the
// pre-formatted text; when omitted it falls back to a signed percent
// of `delta` (the legacy `change` / `compareKeys` contract).
// `entrance` (ronda 14) is OPT-IN (default false) because this chip is
// SHARED with tables, where rows remount on pagination and a per-page
// re-fade would be noise. KPI cards opt in; the fade is delayed
// `MOTION.delayEntrance + 300` so the chip lands AFTER its number.
export function DeltaChip({ delta, label, sentiment, entrance = false, className = '' }) {
  // Hooks before any early return per the motion rules.
  const reduced = usePrefersReducedMotion();
  const entered = useEntrance();
  const s = resolveSentiment(sentiment, delta);
  const hasDelta = typeof delta === 'number' && Number.isFinite(delta);
  const arrow = hasDelta && delta !== 0 ? (delta > 0 ? '▲' : '▼') : null;
  const text = label ?? (hasDelta ? (delta === 0 ? '—' : `${delta > 0 ? '+' : ''}${(+delta).toFixed(1)}%`) : null);
  if (text == null && arrow == null) return null;
  const fg = s === 'good' ? SEMANTIC.bandPositiveText : s === 'bad' ? SEMANTIC.bandNegative : SEMANTIC.neutralText;
  const bg = s === 'good' ? SEMANTIC.bandPositiveBg : s === 'bad' ? SEMANTIC.bandNegativeBg : SEMANTIC.neutralBg;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold leading-tight whitespace-nowrap tabular-nums ${className}`}
      style={{
        color: fg,
        background: bg,
        opacity: !entrance || entered ? 1 : 0,
        // `useEntrance` already carries the `delayEntrance` beat, so an
        // extra 300ms transition-delay lands the chip at
        // `MOTION.delayEntrance + 300` after mount — after its number
        // has started counting.
        transition:
          entrance && !reduced
            ? `opacity ${MOTION.durBase}ms ${MOTION.easeOut} 300ms`
            : undefined,
      }}>
      {arrow && <span aria-hidden="true" className="text-2xs">{arrow}</span>}
      {text}
    </span>
  );
}

// ── Tone — the THRESHOLD vocabulary (epic v3 scorecards) ─────
//
// Deliberately SEPARATE from `Sentiment`, which lives right above:
//
//   Sentiment → the SIGN of a delta        'good' | 'bad' | 'neutral'
//   Tone      → the BAND a value falls in  + 'warn'
//
// They are not merged, and `Sentiment` is not widened, because
// `DeltaChip` (above) and `sentimentBorderStyle` (kpis.jsx) are both
// two-branch ternaries with no 'warn' arm: an authored 'warn' would
// render GREY at those sites. A silently wrong colour is precisely the
// drift this vocabulary exists to remove, so the two stay apart.
//
// Look v3 (DQ2, 2026-10-25) adds 'info', 'info_light' and 'muted': a
// report config names a STATE only by tone (hex colours are refused by
// the engine), and a stacked bar can carry up to seven states, so the
// vocabulary needs seven distinguishable fills.
export const TONES = Object.freeze(['good', 'warn', 'bad', 'info', 'info_light', 'neutral', 'muted']);

// `resolveTone(spec, value)` → Tone.
//
// The ONLY place in the library where a number becomes a colour — and
// only when the CALLER supplied bands. A bare value never implies a
// tone (that is the value-based cell styling this codebase tried and
// rejected); an explicit `spec.tone` always wins. `spec.thresholds` is
// `{min, tone}[]` in AUTHORED order: the first band whose `min` the
// value clears wins, so authors write descending bands.
//
// NEVER THROWS. There is no ErrorBoundary anywhere in the dashboard
// render path, so a throw inside a chart cell is a blank screen, not
// an error card (same class as the React #31 drill-label incident).
// An unmatched value logs loudly and falls back to 'neutral'; the real
// guarantee — that every authored band array ends in a catch-all — is
// enforced at PARSE time in the engine, which already raises per report.
export function resolveTone(spec, value) {
  if (!spec) return 'neutral';
  if (TONES.includes(spec.tone)) return spec.tone;
  const bands = spec.thresholds;
  if (Array.isArray(bands) && bands.length > 0) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      for (const band of bands) {
        // `min: null` IS the catch-all: JSON cannot express -Infinity
        // and every band array arrives as JSON. STRICT null — a band
        // with no `min` at all is malformed, not a catch-all, and the
        // engine's parser rejects it at publish for the same reason.
        const min = band && band.min === null ? -Infinity : (band && band.min);
        if (band && typeof min === 'number' && value >= min && TONES.includes(band.tone)) {
          return band.tone;
        }
      }
      console.error('[resolveTone] no band matched — falling back to neutral', { value, bands });
    }
  }
  return 'neutral';
}

// `toneStyle(tone)` → `{ fg, bg, fill, border }` for a tinted surface.
// `fill` is the saturated colour (meter fills, bar segments); `fg`/`bg`
// are the pastel text/background pair; `border` closes the chip. Every
// value comes from `brand.semantic` — no literal hex lives here, so the
// palette stays changeable in one place.
export function toneStyle(tone) {
  // Band colours are the COLOUR-BLIND-FRIENDLY pair (brand.semantic
  // band*, 2026-07-21): bluish-green vs deep red. The delta vocabulary
  // (positive*/negative*) keeps its historical colours — see brand.js.
  switch (TONES.includes(tone) ? tone : 'neutral') {
    case 'good':
      return { fg: SEMANTIC.bandPositiveText, bg: SEMANTIC.bandPositiveBg, fill: SEMANTIC.bandPositive, border: SEMANTIC.bandPositiveBorder, surface: SEMANTIC.surfaceGood };
    case 'warn':
      return { fg: SEMANTIC.warningText, bg: SEMANTIC.warningBg, fill: SEMANTIC.warning, border: SEMANTIC.warningBorder, surface: SEMANTIC.surfaceWarn };
    case 'bad':
      return { fg: SEMANTIC.bandNegativeText, bg: SEMANTIC.bandNegativeBg, fill: SEMANTIC.bandNegative, border: SEMANTIC.bandNegativeBorder, surface: SEMANTIC.surfaceBad };
    case 'info':
      return { fg: SEMANTIC.infoText, bg: SEMANTIC.infoBg, fill: SEMANTIC.info, border: SEMANTIC.infoBorder, surface: SEMANTIC.surfaceInfo };
    case 'info_light':
      return { fg: SEMANTIC.infoText, bg: SEMANTIC.infoBg, fill: SEMANTIC.infoLight, border: SEMANTIC.infoBorder, surface: SEMANTIC.surfaceInfo };
    case 'muted':
      return { fg: SEMANTIC.neutralText, bg: SEMANTIC.neutralBg, fill: SEMANTIC.muted, border: SEMANTIC.neutralBorder, surface: SEMANTIC.surfaceNeutral };
    default:
      return { fg: SEMANTIC.neutralText, bg: SEMANTIC.neutralBg, fill: NEUTRAL.faint, border: SEMANTIC.neutralBorder, surface: SEMANTIC.surfaceNeutral };
  }
}

// `toneLabelKey(tone)` → the i18n key for the NON-COLOUR channel.
// Threshold information carried only by colour is invisible to a
// colourblind or screen-reader reader — on a report that goes to an
// executive audience. Every scorecard primitive composes this label
// into its accessible name; the consuming app owns the translation
// (`tone.good` / `tone.warn` / `tone.bad` / `tone.neutral`).
export function toneLabelKey(tone) {
  return `tone.${TONES.includes(tone) ? tone : 'neutral'}`;
}

// ── parseValueDelta — strict "value + delta" cell-string parser ──
//
// SQL emits comparison cells as a single formatted string:
//
//   "164,149 ▼ -9.6%"   value + arrow + signed percent delta
//   "96.2% ▲ +0.5 pp"   percent value + delta in percentage points
//   "28 —"              value, explicit "no change / no delta"
//   "181,596"           bare value (first period — no previous)
//
// Returns `{ value, display, dir, delta }`:
//   value   — numeric magnitude of the value part (commas stripped,
//             "%" suffix stripped; "95.7%" → 95.7)
//   display — the value part verbatim as SQL formatted it
//   dir     — 'up' | 'down' from the ▲/▼ arrow, null otherwise
//   delta   — the delta text verbatim ("-9.6%", "+0.5 pp"), '—' for
//             the explicit no-change dash, null when absent
//
// STRICT: anything that doesn't match the grammar above returns
// `null` — the caller must then render the raw string EXACTLY as it
// would without this parser. Never invent, never deform data ("somos
// BI"): a bare "—", free text, malformed grouping ("1,23"), extra
// tokens — all null.
const VALUE_DELTA_RE =
  /^(-?\d{1,3}(?:,\d{3})+(?:\.\d+)?|-?\d+(?:\.\d+)?)(%?)(\s+(?:([▲▼])\s*([+-]?\d+(?:\.\d+)?\s?(?:%|pp))|—))?$/u;

export function parseValueDelta(text) {
  if (typeof text !== 'string') return null;
  const m = VALUE_DELTA_RE.exec(text.trim());
  if (!m) return null;
  const value = Number(m[1].replace(/,/g, ''));
  if (!Number.isFinite(value)) return null;
  const display = m[1] + m[2];
  const dir = m[4] === '▲' ? 'up' : m[4] === '▼' ? 'down' : null;
  const delta = m[5] ?? (m[3] ? '—' : null);
  return { value, display, dir, delta };
}

// ── parseDeltaOnly — bare "arrow + delta" cell-string parser ──
//
// Some SQL columns emit ONLY the delta token in a cell — no value in
// front of it (unlike the trend-matrix "164,149 ▼ -9.6%" cells that
// `parseValueDelta` owns):
//
//   "▲ +2.0%"    up + signed percent
//   "▼ -9.6%"    down + signed percent
//   "▲ +0.5 pp"  up + delta in percentage points
//
// Those land on the DEFAULT cell path (no `render: "trend"` configured),
// so `parseValueDelta` rejects them (it requires a leading value). This
// sibling parser recognizes the bare arrow+delta token so the default
// renderer can draw a `DeltaChip` pill instead of flat coloured text.
//
// Returns `{ dir, delta }`:
//   dir   — 'up' (▲/△) | 'down' (▼/▽) from the arrow glyph
//   delta — the delta text verbatim ("+2.0%", "-9.6%", "+0.5 pp")
//
// STRICT (same contract as parseValueDelta): anything that is not
// EXACTLY an arrow+delta token returns `null`, and the caller must then
// render the raw string UNCHANGED. A bare "—", free text ("NEWEGG"), a
// URL, bilingual JSON, or a "value ▲ delta" string (owned by
// parseValueDelta) — all null. Never invent, never deform ("somos BI").
const DELTA_ONLY_RE =
  /^([▲▼△▽])\s*([+-]?\d+(?:\.\d+)?\s?(?:%|pp))$/u;

export function parseDeltaOnly(text) {
  if (typeof text !== 'string') return null;
  const m = DELTA_ONLY_RE.exec(text.trim());
  if (!m) return null;
  const dir = m[1] === '▲' || m[1] === '△' ? 'up' : 'down';
  return { dir, delta: m[2] };
}

// ── Trend-matrix cell (redesign: "Comparativa por trimestre") ──
//
// Deterministic emoji-dot → bar-color mapping. The metric column of
// the fiscal-comparison SQL prefixes some metric names with a colored
// emoji dot (🔴 unauthorized / 🟢 authorized / 🟣 1P / 🟠 HHO); the
// micro-bar of every trend cell in that row follows the same color.
// Rows without a dot use the first series colour (S1 violet). Tokens
// come from brand.js — no inline hex. Red/green follow the colour-blind
// band pair, never the classic green/red.
export const TREND_DOT_COLORS = {
  '🔴': brand.semantic.bandNegative, // deep red
  '🟢': brand.semantic.bandPositive, // teal
  '🟣': brand.palette[6],            // S7 purple
  '🟠': brand.palette[2],            // S3 amber
};
export const TREND_BAR_NEUTRAL = brand.palette[0]; // S1 violet

// `trendMetricColor(label)` — bar color for a row given its metric
// label (plain string, already locale-resolved). Leading emoji dot
// wins; anything else → neutral dark.
export function trendMetricColor(label) {
  const s = typeof label === 'string' ? label.trim() : '';
  for (const dot of Object.keys(TREND_DOT_COLORS)) {
    if (s.startsWith(dot)) return TREND_DOT_COLORS[dot];
  }
  return TREND_BAR_NEUTRAL;
}

// `TrendCell` — the rich comparison cell (HTML flavor, like
// DeltaChip), compact 2-line layout (redesign feedback r2): line 1 is
// the bold value (right-aligned); line 2 puts the micro-bar (width ∝
// |value| / row max, right-anchored fill) and the semantic ▲/▼ pastel
// delta chip SIDE BY SIDE on the same row (bar left, chip glued to
// its right) so the cell stays low and the chip never floats alone.
// `parsed` is a `parseValueDelta` result — callers parse first (they
// need the values for the row max anyway) and fall back to their
// default cell rendering when it's null. Chip sentiment follows the
// arrow direction (▲ green / ▼ red), matching what the SQL already
// encodes today — metric-inversion semantics are NOT invented
// client-side. Bar geometry is inline-styled so it doesn't depend on
// the consumer's Tailwind content scan.
export function TrendCell({ parsed, max, color, className = '' }) {
  // Entrance (ronda 13): the micro-bar fills from zero on mount. The
  // hooks run unconditionally (before the null guard) per rules of
  // hooks. The DISPLAY string never animates — it is the SQL-formatted
  // value verbatim; re-deriving it from the parsed number could drift
  // from the source formatting (regla BI).
  const entered = useEntrance();
  const reducedMotion = usePrefersReducedMotion();
  if (!parsed) return null;
  const { value, display, dir, delta } = parsed;
  const showBar =
    Number.isFinite(value) && typeof max === 'number' && Number.isFinite(max) && max > 0;
  const barW = showBar ? Math.max(4, Math.round((Math.abs(value) / max) * 52)) : 0;
  // Fixed zones — [value+track column][chip zone 66px] — with the chip
  // zone ALWAYS reserved (even when there is no delta) and a constant
  // 52px track, so bars and chips form straight vertical columns across
  // every row: a variable-width chip must never shift the track (the
  // r2.1 misalignment bug). The chip sits vertically centered beside
  // the value+bar stack, which keeps the cell two text-lines low.
  return (
    <span className={`inline-flex items-center justify-end ${className}`} style={{ gap: 5 }}>
      <span className="inline-flex flex-col items-end">
        <span className="text-[11px] font-bold text-neutral-800 tabular-nums leading-tight">
          {display}
        </span>
        {showBar && (
          <span
            aria-hidden="true"
            className="block overflow-hidden shrink-0"
            style={{ width: 52, height: 4, borderRadius: 3, background: NEUTRAL.grid, marginTop: 3 }}>
            <span
              className="block h-full"
              style={{
                width: barW,
                borderRadius: 3,
                background: color || TREND_BAR_NEUTRAL,
                marginLeft: 'auto',
                // Right-anchored fill → grow from the right edge.
                transform: entered ? 'none' : 'scaleX(0)',
                transformOrigin: '100% 50%',
                transition: reducedMotion
                  ? undefined
                  : `transform ${MOTION.durEntrance}ms ${MOTION.easeOut}`,
              }}
            />
          </span>
        )}
      </span>
      <span className="inline-flex items-center justify-start shrink-0" style={{ width: 66 }}>
        {delta != null && (
          <DeltaChip
            delta={dir === 'up' ? 1 : dir === 'down' ? -1 : 0}
            label={delta}
            className="text-2xs"
          />
        )}
      </span>
    </span>
  );
}

// ── thinLabelIndices — auto-thinning for default-on data labels ──
//
// Redesign rule 1: data labels are ON by default, so dense series
// need automatic thinning to avoid overlap. Returns the Set of
// indices that should carry a label:
//   n ≤ 12  → all points
//   n ≤ 20  → every other point + extremes + peaks
//   n > 20  → extremes (first/last) + peaks (global max + min) only
// ── X-axis label stride (staging 2026-10-23) ────────────────────
// Every N-th X label is drawn. The charts used a COUNT cap alone
// (``ceil(n / 8)``), so 13 ISO dates in a third of the page were drawn
// 47 px apart while each needs ~70 px, and they ran into one another
// ("2026-06-092026-06-23"). The stride is now the larger of that cap
// and what the plot WIDTH holds: ~6 px per character at fontSize 10
// plus a 10 px gap per label. Labels that fit keep the old density.
export function xLabelStride(labels, plotWidth, maxLabels) {
  const n = Array.isArray(labels) ? labels.length : 0;
  if (n === 0) return 1;
  const byCount = Math.ceil(n / maxLabels);
  const longest = labels.reduce((m, l) => Math.max(m, String(l ?? '').length), 0);
  const fit = Math.max(1, Math.floor((Number.isFinite(plotWidth) ? plotWidth : 0) / (longest * 6 + 10)));
  return Math.max(1, byCount, Math.ceil(n / fit));
}

// ── Category label fitted to its slot (develop 2026-10-23) ──────
// A vertical bar's category label is centered under its bar and may use
// its whole slot (cw / n). Twelve SKU names in half a page ran into one
// another ("SKU–Retailer Pairs Out Longest"); a label longer than its slot
// now ends in "…" (~6 px per character at fontSize 10) and keeps its full
// text for a native <title> (hover).
export function fitSlotLabel(label, slotPx) {
  const text = String(label ?? '');
  const room = Math.max(1, Math.floor(((Number.isFinite(slotPx) ? slotPx : 0) - 4) / 6));
  if (text.length <= room) return { text, full: null };
  return { text: room <= 1 ? '…' : `${text.slice(0, room - 1)}…`, full: text };
}

// The <text> of a category label fitted to its slot (``fitSlotLabel``):
// the cut label, and the whole one in a <title>.
export function SlotLabel({ label, slot, ...textProps }) {
  const { text, full } = fitSlotLabel(label, slot);
  return (
    <text {...textProps}>
      {text}
      {full !== null && <title>{full}</title>}
    </text>
  );
}

export function thinLabelIndices(values) {
  const n = Array.isArray(values) ? values.length : 0;
  const idx = new Set();
  if (n === 0) return idx;
  if (n <= 12) {
    for (let i = 0; i < n; i++) idx.add(i);
    return idx;
  }
  idx.add(0);
  idx.add(n - 1);
  let maxI = -1, minI = -1, maxV = -Infinity, minV = Infinity;
  values.forEach((v, i) => {
    if (typeof v === 'number' && Number.isFinite(v)) {
      if (v > maxV) { maxV = v; maxI = i; }
      if (v < minV) { minV = v; minI = i; }
    }
  });
  if (maxI >= 0) idx.add(maxI);
  if (minI >= 0) idx.add(minI);
  if (n <= 20) {
    for (let i = 0; i < n; i += 2) idx.add(i);
  }
  return idx;
}

// Extremes (first/last) + global max/min only — the label set that
// multi-series line charts use when 3+ series share the plane
// (rule 1 dense guard), regardless of series length.
export function peakLabelIndices(values) {
  const n = Array.isArray(values) ? values.length : 0;
  const idx = new Set();
  if (n === 0) return idx;
  idx.add(0);
  idx.add(n - 1);
  let maxI = -1, minI = -1, maxV = -Infinity, minV = Infinity;
  values.forEach((v, i) => {
    if (typeof v === 'number' && Number.isFinite(v)) {
      if (v > maxV) { maxV = v; maxI = i; }
      if (v < minV) { minV = v; minI = i; }
    }
  });
  if (maxI >= 0) idx.add(maxI);
  if (minI >= 0) idx.add(minI);
  return idx;
}

// ── SVG legend-table (redesign rule 3) ───────────────────────
//
// "Legends that inform": swatch + series name + last value (bold) +
// semantic delta chip (▲/▼ pastel pill, rule 4) instead of bare
// 6px dots. Rendered inside the chart SVG so the chart's `height`
// budget accounts for it (charts reserve `legendLayout(...).height`
// in their bottom padding).
//
// `items`: [{ name, color, shape?: 'square'|'line', value?: string,
//             delta?: number|null }]
//   `value` is the pre-formatted last value of the series.
//   `delta` is the fractional change vs the previous point
//   (e.g. 0.042 → "▲ 4.2%"). null/undefined → no chip (unknown ≠ 0).
//
// `legendLayout(items, width)` measures + wraps rows (approximate
// char-width metrics, same technique the lib already uses for axis
// padding) and returns `{ rows, rowH, height }`. Pass the result to
// `<SVGLegendRows layout={...} width={w} top={y} />`.
const LEGEND_ROW_H = 18;
// Inter-entry breathing room (2026-07 legend polish): entries used to
// sit 14px apart with per-char estimates that UNDER-measured the bold
// value + delta chip, so dense legends ("NO 1.2K ▼0.1% · YES 100 …")
// read glued / half-overlapped. The estimates below are calibrated to
// the actual font metrics (name 10px ≈ 5.8px/char, value 11px bold ≈
// 7px/char, chip 8.5px bold ≈ 5.8px/char) and the gap is 18px.
const LEGEND_ENTRY_GAP = 18;
// A flat delta (0) keeps its '—' text so the entry MEASURES like one with
// a chip — the columns of a legend stay aligned — but `SVGLegendRows`
// does not draw it: a grey dash pill next to every flat series read as
// noise (JP, 2026-10-23). The slot stays, invisible.
function legendDeltaText(delta) {
  if (delta === null || delta === undefined || !Number.isFinite(delta)) return null;
  if (delta === 0) return '—';
  return `${delta > 0 ? '▲' : '▼'} ${Math.abs(delta * 100).toFixed(1)}%`;
}
// Single source for the per-entry zone widths — `legendLayout` stores
// them on each measured item and `SVGLegendRows` reads them back, so
// the wrap math and the render can never drift apart.
function legendEntryZones(it, chip) {
  const nameW = String(it.name ?? '').length * 6.4 + 24; // swatch + gaps
  const valueW = it.value != null ? String(it.value).length * 7 + 8 : 0;
  const chipW = chip != null ? chip.length * 6.8 + 16 : 0;
  return { nameW, valueW, chipW };
}
export function legendLayout(items, width) {
  const measured = (items || []).map(it => {
    const chip = legendDeltaText(it.delta);
    const { nameW, valueW, chipW } = legendEntryZones(it, chip);
    return { ...it, chip, nameW, valueW, chipW, w: Math.ceil(nameW + valueW + chipW) + LEGEND_ENTRY_GAP };
  });
  const maxW = Math.max(60, width || 0);
  const rows = [];
  let cur = [], curW = 0;
  for (const m of measured) {
    if (cur.length && curW + m.w > maxW) { rows.push(cur); cur = []; curW = 0; }
    cur.push(m);
    curW += m.w;
  }
  if (cur.length) rows.push(cur);
  return { rows, rowH: LEGEND_ROW_H, height: rows.length * LEGEND_ROW_H + 6 };
}
// `onToggle(index)` (opt-in) makes each entry a button that hides /
// shows its series: a hidden entry stays in the legend, attenuated
// (never removed — the series still exists). `index` is the entry's
// position in the items passed to `legendLayout`.
export function SVGLegendRows({ layout, width, top, onToggle }) {
  if (!layout || !layout.rows.length) return null;
  const toggles = typeof onToggle === 'function';
  let flat = -1;
  return (
    <g aria-hidden={toggles ? undefined : 'true'}>
      {layout.rows.map((row, ri) => {
        // The trailing entry's inter-entry gap doesn't take horizontal
        // space — exclude it so the row centers on its VISIBLE width.
        const rowW = row.reduce((s, m) => s + m.w, 0) - LEGEND_ENTRY_GAP;
        let x = Math.max(0, (width - rowW) / 2);
        const y = top + ri * layout.rowH;
        return row.map((m, mi) => {
          const gx = x;
          x += m.w;
          const { nameW, valueW, chipW } = m;
          // A drawn chip is never flat (0 keeps only its slot, see legendDeltaText).
          const up = (m.delta ?? 0) > 0;
          const chipFg = up ? SEMANTIC.bandPositiveText : SEMANTIC.bandNegative;
          const chipBg = up ? SEMANTIC.bandPositiveBg : SEMANTIC.bandNegativeBg;
          flat += 1;
          const idx = m.index ?? flat;
          const interactive = toggles ? {
            role: 'button', tabIndex: 0, 'aria-pressed': !m.hidden, 'aria-label': m.name,
            'data-legend-toggle': idx, style: { cursor: 'pointer' },
            onClick: () => onToggle(idx),
            onKeyDown: e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(idx); } },
          } : {};
          return (
            <g key={`${ri}-${mi}`} transform={`translate(${gx},${y})`} opacity={m.hidden ? 0.35 : 1} {...interactive}>
              {toggles && <rect x="-2" y="0" width={m.w - LEGEND_ENTRY_GAP + 4} height="15" fill="transparent"/>}
              {m.shape === 'line' ? (
                <line x1="0" y1="7.5" x2="12" y2="7.5" stroke={m.color} strokeWidth="2.5" strokeLinecap="round" strokeDasharray={m.dashed ? '4 3' : undefined}/>
              ) : (
                <rect y="3" width="9" height="9" rx="2.5" fill={m.color}/>
              )}
              <text x="15" y="11" fontSize="11" fill={NEUTRAL.muted}>{m.name}</text>
              {m.value != null && (
                <text x={nameW} y="11" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{m.value}</text>
              )}
              {m.chip != null && m.delta !== 0 && (
                <g transform={`translate(${nameW + valueW},0)`}>
                  <rect y="0.5" width={chipW - 4} height="15" rx="7.5" fill={chipBg}/>
                  <text x={(chipW - 4) / 2} y="11" textAnchor="middle" fontSize="11" fontWeight="700" fill={chipFg}>{m.chip}</text>
                </g>
              )}
            </g>
          );
        });
      })}
    </g>
  );
}

// ── MultiSeriesTooltip — default hover layout for multi-series charts ──
//
// Used by MultiLineT, ComboChartT and GroupedBarT (stacked mode) so the
// hover popover renders consistently across the lib: bold date / label
// header, one row per series with a colored dot + name + right-aligned
// value, and a separator + total row when there are ≥ 2 series.
//
// Mirrors the Chart.js legacy gating dashboard tooltip so reports
// migrated from there don't lose visual fidelity.
//
// Args:
//   label: header text (typically the x-axis label for the hovered point)
//   series: array of { name, value, color } — one entry per series
//   showTotal: when true (default) and series.length > 1, append a Total
//     row with the sum of values. Set to false for charts where a sum
//     makes no semantic sense (e.g. bar + line with different units).
//   valueFmt: formatter for numeric values, defaults to fmtN.
export function MultiSeriesTooltip({
  label,
  series,
  showTotal = true,
  valueFmt = fmtN,
}) {
  const total = series.reduce((sum, s) => sum + (Number(s.value) || 0), 0);
  return (
    <div className="flex flex-col gap-0.5 min-w-[140px]">
      <div className="font-semibold text-white text-[12px] mb-1">{label}</div>
      {series.map((s, si) => (
        <div key={si} className="flex items-center gap-2 text-[11px]">
          <span
            className="inline-block w-2 h-2 rounded-full shrink-0"
            style={{ background: s.color }}
          />
          <span className="text-white/90">{s.name}:</span>
          <span className="ml-auto font-semibold tabular-nums text-white">
            {typeof s.value === 'string' ? s.value : valueFmt(s.value)}
          </span>
        </div>
      ))}
      {showTotal && series.length > 1 && (
        <div className="border-t border-white/20 mt-1 pt-1 flex items-center gap-2 text-[11px]">
          <span className="text-white/80">Total:</span>
          <span className="ml-auto font-semibold tabular-nums text-white">
            {valueFmt(total)}
          </span>
        </div>
      )}
    </div>
  );
}


// ── ChartCard vertical chrome (uniform-grid contract) ─────────
//
// Measured from the ChartCard markup right below — if you change the
// card's paddings/typography, THIS constant must move with it (review
// #12: the constant lives next to the markup it measures):
//
//   card border      2px (1 top + 1 bottom)
//   header pt-4     16px
//   title line      20px (text-sm ⇒ 20px line-height, single line)
//   header pb-2      8px
//   body pb-4       16px  (body has no top padding)
//   ─────────────────────
//   total           62px
//
// MIRROR consumer: packages/dashboard-render/src/GenericChart.tsx
// derives the SVG plot height as `cellPx − CHART_CARD_CHROME_PX` so
// the card lands EXACTLY on its grid cell. (A two-line truncated title
// never happens — the header `truncate`s to one line by design.)
export const CHART_CARD_CHROME_PX = 62;

// `fill` (uniform-grid PR-2, OPT-IN): the card stretches to its grid
// cell (`h-full flex flex-col`) with the body claiming the leftover
// space (`flex-1 min-h-0`) and clipping overflow. Default (false)
// keeps the historical intrinsic-height card — DrillSection,
// RowDrillPanel and the showcase render without the prop and are
// byte-identical to before.
export function ChartCard({ title, subtitle, children, className = '', action = null, fill = false, info = null }) {
  return (
    // Look v3 (2026-10-24): the approved card, one radius and one border for
    // every surface; no hover lift (motion only for state).
    <div data-chart-card="" className={`rounded-[14px] border border-neutral-200 bg-white shadow-card ${fill ? 'h-full flex flex-col overflow-hidden' : ''} ${className}`}>
      <div className="px-5 pt-4 pb-2 flex items-start justify-between gap-2 shrink-0">
        <div className="min-w-0">
          {/* Info icon sits INLINE in the single title line — the header
              stays one line tall so CHART_CARD_CHROME_PX (62) holds. */}
          <div className="flex min-w-0 items-center gap-1.5">
            <h3 className="text-sm font-bold text-neutral-800 truncate">{title}</h3>
            {info && <InfoTip text={info} className="text-neutral-400" />}
          </div>
          {subtitle && <p className="text-xs text-neutral-600 mt-0.5 truncate">{subtitle}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <div className={`px-4 pb-4 flex-1 flex flex-col${fill ? ' min-h-0' : ''}`}>{children}</div>
    </div>
  );
}

// ── InfoTip (2026-07 — "tooltips en todos los títulos") ──────
//
// Discreet ⓘ affordance next to a title. Opens on hover AND keyboard
// focus (a native `title` attr is hover-only, which is why this
// primitive exists) and closes on Escape.
//
// The bubble renders through a PORTAL on `document.body`, positioned
// with fixed coordinates measured from the icon. That is the only
// escape from the dashboard's own layout: every `fill` card, the
// mini-table cards and the navy SectionHeader band CLIP their content
// (`overflow-hidden`), and the hover-lift transforms open stacking
// contexts — against clipping no z-index can win (the CSS-only
// version was cut off by both). Placement flips above the icon when
// the bubble would fall past the viewport bottom and clamps
// horizontally at the edges, with the caret tracking the icon.
//
// The icon inherits `currentColor` so it adapts to light card headers
// and the dark navy band alike (the caller sets it via `className`);
// the bubble is always brand navy (`primary-900`) so the tooltip
// reads identically on every surface.
// ── CursorTip — cursor-anchored data tooltip, PORTALED ──────────
//
// Same hard rule as InfoTip (rule 17): cards clip (`overflow-hidden`)
// and scrollers grow (`overflow-y-auto` gains a second scrollbar when
// an absolutely-positioned tip extends past the bottom — observed live
// on the CHS card grid). So a hover readout NEVER renders inside the
// card: it portals to document.body with FIXED coords, flips above the
// cursor when there is no room below, and clamps horizontally.
// Internal helper (SHOWCASE_EXCLUDED) — consumed by StackedShareBar
// and MeterBar.
export function CursorTip({ x, y, children }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, ready: false });
  useLayoutEffect(() => {
    const t = ref.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const M = 8;
    const left = Math.min(
      Math.max(x - r.width / 2, M),
      Math.max(M, window.innerWidth - M - r.width),
    );
    const above = y - r.height - 14 >= M;
    const top = above ? y - r.height - 10 : y + 14;
    setPos({ left, top, ready: true });
  }, [x, y, children]);
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={ref}
      role="tooltip"
      className="pointer-events-none fixed z-[1000]"
      style={{ left: pos.left, top: pos.top, visibility: pos.ready ? 'visible' : 'hidden' }}
    >
      <div className="whitespace-nowrap rounded-md bg-neutral-900/95 px-2.5 py-1.5 text-[11px] font-medium text-white shadow-xl ring-1 ring-white/10">
        {children}
      </div>
    </div>,
    document.body,
  );
}

let _infoTipSeq = 0;

export function InfoTip({ text, className = '', strong = false }) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState('below');
  const btnRef = useRef(null);
  const tipRef = useRef(null);
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `pm-infotip-${++_infoTipSeq}`;
  const reduce = usePrefersReducedMotion();

  // Measure → place. Runs before paint and again on scroll/resize, so
  // the bubble stays glued to its icon while the page moves under it.
  const place = useCallback(() => {
    const btn = btnRef.current;
    const tip = tipRef.current;
    if (!btn || !tip) return;
    const a = btn.getBoundingClientRect();
    const t = tip.getBoundingClientRect();
    const M = 8;    // viewport margin
    const GAP = 10; // icon → bubble
    const below = a.bottom + GAP + t.height <= window.innerHeight - M;
    const top = below ? a.bottom + GAP : a.top - GAP - t.height;
    const cx = a.left + a.width / 2;
    const left = Math.min(
      Math.max(cx - t.width / 2, M),
      Math.max(M, window.innerWidth - M - t.width),
    );
    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(Math.max(M, top))}px`;
    // The caret keeps pointing at the icon even when the bubble had
    // to be clamped sideways (icons near the viewport edge).
    const caret = Math.min(Math.max(cx - left, 16), Math.max(16, t.width - 16));
    tip.style.setProperty('--pm-tip-caret', `${Math.round(caret)}px`);
    setPlacement(below ? 'below' : 'above');
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    ensureMotionStyles();
    place();
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!text) return null;

  return (
    <span className={`inline-flex shrink-0 align-middle ${className}`}>
      <button
        ref={btnRef}
        type="button"
        aria-label={text}
        aria-describedby={open ? idRef.current : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        // The ⓘ often sits inside a drillable card — never let the
        // click reach it.
        onClick={e => { e.preventDefault(); e.stopPropagation(); }}
        // `strong` (ronda 11, feedback "demasiado tenue"): full-opacity
        // resting state for ⓘs that must be findable at a glance — the
        // default keeps the historical subdued 50%.
        className={`inline-flex h-4 w-4 items-center justify-center rounded-full ${strong ? 'opacity-90' : 'opacity-50'} transition duration-150 hover:opacity-100 hover:text-accent-dark focus-visible:opacity-100 focus-visible:text-accent-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60`}
      >
        <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true">
          <circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <rect x="7.25" y="6.9" width="1.5" height="4.2" rx="0.75" fill="currentColor" />
          <circle cx="8" cy="4.8" r="0.95" fill="currentColor" />
        </svg>
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={tipRef}
          id={idRef.current}
          role="tooltip"
          className="pointer-events-none fixed left-0 top-0 z-[1000] max-w-[300px] rounded-xl bg-navy px-3.5 py-2.5 text-left text-[13px] font-normal normal-case leading-relaxed tracking-normal text-white/95 shadow-xl ring-1 ring-white/10"
          style={reduce ? undefined : { animation: `pmRiseIn ${MOTION.durFast}ms ${MOTION.easeOut} both` }}
        >
          <span
            aria-hidden="true"
            className={`absolute h-2.5 w-2.5 -translate-x-1/2 rotate-45 rounded-[1px] bg-navy ${placement === 'below' ? '-top-1' : '-bottom-1'}`}
            style={{ left: 'var(--pm-tip-caret, 50%)' }}
          />
          {text}
        </div>,
        document.body,
      )}
    </span>
  );
}
